// src/hooks/results/useMeasurementResults.js
import { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { documentId } from "firebase/firestore";
import { useTranslation } from "react-i18next";
import { mapMeasurementDocToRow } from "./mapMeasurementRow";
import {
  getInitialRealtimeListenLimit,
  getNextRealtimeListenLimit,
  selectRealtimeMeasurementDocs,
  shouldExpandRealtimeListenLimit,
} from "./loadStagedMeasurementDocs";

export function useMeasurementResults(
  db,
  uid,
  {
    collectionName,
    context = null,
    pageSize = 50,
    orderField = "documentId",
    orderDirection = "desc",
  },
) {
  const { i18n } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(!!db && !!uid);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState(null);
  const hasLoadedSnapshotRef = useRef(false);
  const pageSizeRef = useRef(pageSize);

  const colRef = useMemo(() => {
    if (!db || !uid) return null;
    return collection(db, "measurements", uid, collectionName);
  }, [db, uid, collectionName]);

  const initialListenLimit = useMemo(
    () => getInitialRealtimeListenLimit(pageSize, false),
    [pageSize],
  );
  const [listenLimit, setListenLimit] = useState(initialListenLimit);

  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  useEffect(() => {
    hasLoadedSnapshotRef.current = false;
    setListenLimit(getInitialRealtimeListenLimit(pageSizeRef.current, false));
    setLoading(!!colRef);
    setLoadingMore(false);
    setHasMore(false);
    setError(null);
  }, [colRef, context]);

  useEffect(() => {
    setListenLimit((currentLimit) =>
      Math.max(currentLimit, getInitialRealtimeListenLimit(pageSize, false)),
    );

    if (hasLoadedSnapshotRef.current) {
      setLoadingMore(true);
    }
  }, [pageSize]);

  useEffect(() => {
    if (!colRef) {
      setRows([]);
      setLoading(false);
      setLoadingMore(false);
      setHasMore(false);
      setError(null);
      return;
    }

    let cancelled = false;

    if (!hasLoadedSnapshotRef.current) {
      setLoading(true);
      setLoadingMore(false);
    }
    setError(null);

    const baseConstraints = [];
    if (context) {
      baseConstraints.push(where("appMode", "==", context));
    }
    if (orderField === "documentId") {
      baseConstraints.push(orderBy(documentId(), orderDirection));
    } else {
      baseConstraints.push(orderBy(orderField, orderDirection));
    }

    const qy = query(colRef, ...baseConstraints, limit(listenLimit));

    const unsub = onSnapshot(
      qy,
      { includeMetadataChanges: true },
      (snap) => {
        if (cancelled) return;

        const docs = selectRealtimeMeasurementDocs(snap.docs, pageSize);

        setRows(
          docs.map((doc) =>
            mapMeasurementDocToRow(
              doc,
              context,
              i18n.resolvedLanguage || i18n.language || "en-GB",
            ),
          ),
        );
        hasLoadedSnapshotRef.current = true;
        setLoading(false);
        setHasMore(snap.size >= listenLimit);

        const needsExpansion = shouldExpandRealtimeListenLimit(
          snap.size,
          listenLimit,
          docs.length,
          pageSize,
        );

        setLoadingMore(needsExpansion);

        if (needsExpansion) {
          setListenLimit((currentLimit) =>
            getNextRealtimeListenLimit(currentLimit, pageSize, false),
          );
        }
      },
      (err) => {
        if (cancelled) return;
        console.error("useMeasurementResults snapshot error", {
          collectionName,
          uid,
          context,
          orderField,
          orderDirection,
          error: err,
        });
        setError(err);
        setLoading(false);
        setLoadingMore(false);
        setHasMore(false);
      },
    );

    return () => {
      cancelled = true;
      unsub();
    };
  }, [
    colRef,
    pageSize,
    context,
    listenLimit,
    collectionName,
    uid,
    orderField,
    orderDirection,
    i18n.resolvedLanguage,
    i18n.language,
  ]);

  return { rows, loading, loadingMore, hasMore, error };
}
